import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import { 
  BookOpen, Lock, Check, ChevronRight, ArrowLeft, 
  PlayCircle, FileText, Crown
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

const MODULES = [
  { id: 1, title: "Why Bitcoin", sections: [1, 2] },
  { id: 2, title: "What Bitcoin Is", sections: [3, 4, 5] },
  { id: 3, title: "How It Works", sections: [6, 7, 8, 9, 10] },
  { id: 4, title: "How To Use It", sections: [11, 12, 13, 14, 15, 16] },
  { id: 5, title: "Mindset", sections: [17, 18, 19, 20, 21] }
];

export default function Course() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(1);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const res = await api.get("/course/sections");
        setSections(res.data.sections);
        
        const currentSection = res.data.current_section;
        const module = MODULES.find(m => m.sections.includes(currentSection));
        if (module) setActiveModule(module.id);
      } catch (error) {
        console.error("Error fetching sections:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSections();
  }, []);

  const completedCount = sections.filter(s => s.completed).length;
  const progress = (completedCount / 21) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
            <div className="flex items-center gap-3">
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-8 h-8 rounded-lg" />
              <span className="font-heading text-lg font-bold text-white">
                THE <span className="text-[#F7931A]">COURSE</span>
              </span>
            </div>
            <div className="text-sm text-zinc-500">
              {completedCount}/21
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Educator full-access banner */}
        {user?.purchase_tier === "educator" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-[#F7931A]/5 border border-[#F7931A]/20 rounded-xl p-4 flex items-center gap-3"
            data-testid="educator-course-banner"
          >
            <Crown className="text-[#F7931A] flex-shrink-0" size={18} />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">All 21 sections are unlocked</p>
              <p className="text-zinc-500 text-xs">We recommend going through the course in order for the best learning experience.</p>
            </div>
          </motion.div>
        )}

        {/* Progress Overview */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="font-heading text-2xl font-bold text-white">
              Your Progress
            </h1>
            <span className="text-[#F7931A] font-mono">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-3" />
        </div>

        {/* Module Navigation */}
        <div className="flex overflow-x-auto gap-2 mb-8 pb-2">
          {MODULES.map((module) => {
            const moduleSections = sections.filter(s => module.sections.includes(s.id));
            const completedInModule = moduleSections.filter(s => s.completed).length;
            const isActive = activeModule === module.id;
            
            return (
              <button
                key={module.id}
                onClick={() => setActiveModule(module.id)}
                className={`flex-shrink-0 px-4 py-3 rounded-lg border transition-all ${
                  isActive 
                    ? "bg-[#F7931A]/10 border-[#F7931A] text-[#F7931A]" 
                    : "bg-[#0A0A0A] border-white/10 text-zinc-400 hover:border-[#F7931A]/50"
                }`}
              >
                <div className="text-left">
                  <p className="text-xs opacity-60">Module {module.id}</p>
                  <p className="font-medium">{module.title}</p>
                  <p className="text-xs mt-1">{completedInModule}/{module.sections.length}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Sections List */}
        <div className="space-y-3">
          {MODULES.find(m => m.id === activeModule)?.sections.map((sectionId, index) => {
            const section = sections.find(s => s.id === sectionId);
            if (!section) return null;
            
            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`card-dark p-4 sm:p-6 ${section.locked ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    section.completed 
                      ? "bg-green-500/20 text-green-400" 
                      : section.locked 
                        ? "bg-zinc-800 text-zinc-600" 
                        : "bg-[#F7931A]/20 text-[#F7931A]"
                  }`}>
                    {section.completed ? (
                      <Check size={24} />
                    ) : section.locked ? (
                      <Lock size={24} />
                    ) : (
                      <span className="font-heading font-bold text-xl">{section.id}</span>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading text-lg font-bold text-white truncate">
                        {section.title}
                      </h3>
                    </div>
                    <p className="text-zinc-500 text-sm line-clamp-2">
                      {section.locked && section.unlock_requirement
                        ? section.unlock_requirement
                        : section.hook}
                    </p>
                  </div>
                  
                  <div className="flex-shrink-0">
                    {section.locked ? (
                      section.lock_reason === "free_tier" ? (
                        <Button 
                          onClick={() => navigate("/pricing")}
                          variant="outline"
                          size="sm"
                          className="border-[#F7931A] text-[#F7931A]"
                          data-testid={`section-${section.id}-unlock-btn`}
                        >
                          Upgrade
                        </Button>
                      ) : (
                        <div className="flex items-center gap-1.5 text-zinc-500 text-sm">
                          <Lock size={14} />
                          <span className="hidden sm:inline">Locked</span>
                        </div>
                      )
                    ) : (
                      <Button 
                        onClick={() => navigate(`/course/section/${section.id}`)}
                        className="btn-primary"
                        data-testid={`section-${section.id}-btn`}
                      >
                        {section.completed ? "Review" : "Start"} <ChevronRight size={18} />
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Upgrade prompt for free users only */}
        {user?.purchase_tier === "free" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 p-6 bg-gradient-to-br from-[#F7931A]/10 to-transparent border border-[#F7931A]/30 rounded-xl"
          >
            <div className="flex items-center gap-4">
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-16 h-16 rounded-xl" />
              <div className="flex-1">
                <h3 className="font-heading text-xl font-bold text-white">
                  Unlock All 21 Sections
                </h3>
                <p className="text-zinc-400 text-sm">
                  Upgrade to access the complete Bitcoin education journey
                </p>
              </div>
              <Button 
                onClick={() => navigate("/pricing")}
                className="btn-primary"
              >
                View Plans
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
